// Retention workflow constants. Source of truth: public/docs/RETENTION_WORKFLOW.md (§1, §3, §5.4)
// and public/docs/RETENTION_WORKFLOW_TABLES.md (§5 code registry). Keep the codes here in step
// with those docs; do not re-declare them inside components.

import { PHASE_TOKENS } from '../theme/retentionTheme';

export const PHASES = {
  1: { label: 'Onboarding', icon: 'solar:user-plus-rounded-bold-duotone', color: PHASE_TOKENS[1].main, light: PHASE_TOKENS[1].light, soft: PHASE_TOKENS[1].soft, gradient: PHASE_TOKENS[1].gradient },
  2: { label: 'Demo & admission', icon: 'solar:videocamera-record-bold-duotone', color: PHASE_TOKENS[2].main, light: PHASE_TOKENS[2].light, soft: PHASE_TOKENS[2].soft, gradient: PHASE_TOKENS[2].gradient },
  3: { label: 'Tracking & retention', icon: 'solar:refresh-circle-bold-duotone', color: PHASE_TOKENS[3].main, light: PHASE_TOKENS[3].light, soft: PHASE_TOKENS[3].soft, gradient: PHASE_TOKENS[3].gradient },
};

export const STAGE_OWNER = { 1: 'CRO', 2: 'Tutor', 3: 'Parent', 4: 'System', 5: 'Mixed' };

export const TOTAL_STAGES = 13;

// Stage 7 is the real admission: it is the existing "Admission Confirmed" student status (cro_lead_status = 1).
export const GO_LIVE_STAGE = 7;
export const ADMISSION_CONFIRMED = 'Admission Confirmed';

// Mirrors the retention_stages lookup (§5.4). Stage 7's name matches cro_lead_statuses id 1 on purpose.
export const RETENTION_STAGES = [
  { no: 1, name: 'Basic Details Collected', short: 'Verify basics', phase: 1, owner: 1, tab: 'onboarding', icon: 'solar:user-check-rounded-bold-duotone' },
  { no: 2, name: 'First CRO Call Done', short: 'First call', phase: 1, owner: 1, tab: 'onboarding', icon: 'solar:phone-calling-rounded-bold-duotone' },
  { no: 3, name: 'Induction Booked', short: 'Induction', phase: 1, owner: 5, tab: 'onboarding', icon: 'solar:presentation-graph-bold-duotone' },
  { no: 4, name: 'Tutor Mapped', short: 'Tutor mapping', phase: 1, owner: 5, tab: 'onboarding', icon: 'solar:users-group-two-rounded-bold-duotone' },
  { no: 5, name: 'Demo Scheduled', short: 'Demo class', phase: 2, owner: 5, tab: 'demo', icon: 'solar:videocamera-record-bold-duotone' },
  { no: 6, name: 'Demo Completed', short: 'Demo feedback', phase: 2, owner: 5, tab: 'demo', icon: 'solar:star-bold-duotone' },
  { no: 7, name: ADMISSION_CONFIRMED, short: 'Go-live', phase: 2, owner: 1, tab: 'demo', icon: 'solar:verified-check-bold-duotone' },
  { no: 8, name: 'First Feedback Done', short: '1st feedback', phase: 3, owner: 5, tab: 'tracking', icon: 'solar:chat-round-like-bold-duotone' },
  { no: 9, name: 'Mid Progress Report Shared', short: 'Mid report', phase: 3, owner: 5, tab: 'tracking', icon: 'solar:chart-2-bold-duotone' },
  { no: 10, name: 'Feedback Report Shared', short: 'Feedback report', phase: 3, owner: 5, tab: 'tracking', icon: 'solar:document-text-bold-duotone' },
  { no: 11, name: 'Comparison Report Shared', short: 'Comparison', phase: 3, owner: 5, tab: 'tracking', icon: 'solar:graph-up-bold-duotone' },
  { no: 12, name: 'Retention Call Done', short: 'Retention call', phase: 3, owner: 1, tab: 'tracking', icon: 'solar:phone-rounded-bold-duotone' },
  { no: 13, name: 'Retained And Renewed', short: 'Renewal', phase: 3, owner: 5, tab: 'tracking', icon: 'solar:cup-star-bold-duotone' },
];

export const stageByNo = (no) => RETENTION_STAGES.find((s) => s.no === Number(no));

// Workflow stage vs student (sales) status — RETENTION_WORKFLOW.md §3 (decision 2026-09-28):
// - cro_assignment.retention_stage_no (1–13) is the stage; only advance-stage writes it.
// - cro_lead_status = 1 ("Admission Confirmed") IS stage 7 Go-live, so an admitted student is at stage 7 or later
//   even before retention_stage_no is written (or if it was set from the old status dialog).
// - Every other CRO lead is in the workflow from stage 1 (Verify basics).
const isAdmissionConfirmed = (row) =>
  String(row?.student_status || '').trim().toLowerCase() === ADMISSION_CONFIRMED.toLowerCase();

export const getWorkflowStageNo = (row) => {
  const no = Number(row?.retention_stage_no);
  const stored = no >= 1 && no <= TOTAL_STAGES ? no : null;
  if (isAdmissionConfirmed(row)) return Math.max(stored || 0, GO_LIVE_STAGE);
  return stored || 1;
};

// Every CRO lead with an enrolment is in the workflow (it starts at Verify basics, before admission).
export const isInWorkflow = (row) => Boolean(row?.course_enrollment_id);

// Enroll Subject (creates the subject enrollments) is no longer gated on Admission Confirmed: tutor mapping (stage 4)
// and the demo come before admission, and both need subject enrollments. The workspace places it on the stage 3 card.
export const canEnrollStudent = (row) => isInWorkflow(row);

export const WORKSPACE_TABS = [
  { value: '', label: 'Home', icon: 'solar:home-2-bold-duotone' },
  { value: 'onboarding', label: 'Onboarding', icon: 'solar:user-plus-rounded-bold-duotone', stages: '1–4' },
  { value: 'demo', label: 'Demo', icon: 'solar:videocamera-record-bold-duotone', stages: '5–7' },
  { value: 'tracking', label: 'Learning Journey', icon: 'solar:refresh-circle-bold-duotone', stages: '8–13' },
  { value: 'timeline', label: 'Timeline', icon: 'solar:history-bold-duotone' },
];

// retention_stage_progress.stage_status (TABLES §5.1)
export const STAGE_STATUS = {
  0: { label: 'Not started', color: 'default' },
  1: { label: 'In progress', color: 'warning' },
  2: { label: 'Completed', color: 'success' },
  3: { label: 'Skipped', color: 'default' },
  4: { label: 'Looped back', color: 'error' },
};

// retention_demo_attempt.mapping_status (TABLES §5.7)
export const MAPPING_STATUS = {
  0: { label: 'Pending', color: 'default' },
  1: { label: 'Matched', color: 'warning' },
  2: { label: 'Confirmed', color: 'success' },
  3: { label: 'Rejected', color: 'error' },
};

// retention_progress_report.report_type (TABLES §5.8)
export const REPORT_TYPE = {
  1: 'Mid progress',
  2: 'Feedback + suggestions',
  3: 'Comparison',
  4: 'Tutor 3-session check-in',
};

// retention_parent_touchpoint.touchpoint_type (TABLES §5.10)
export const TOUCHPOINT_TYPE = {
  1: '1st feedback (parent)',
  2: 'Retention call attempt',
  3: 'Unhappy, not retained',
  4: 'Happy, leaving',
};

// retention_stage_progress.waiting_on (TABLES §5.15)
export const WAITING_ON = { 1: 'CRO', 2: 'Tutor', 3: 'Parent', 4: 'System' };
